import React, { useState } from "react"
import Navbar from "../../components/Navbar/Navbar"
import {
  Table,
  Select,
  Button,
  Modal,
  Row,
  Col,
  Form,
  DatePicker,
  TimePicker,
  Space,
  Input,
  Checkbox,
  Tabs,
} from "antd"
import Title from "antd/es/typography/Title"
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import moment from "moment"
import { UseGetRequest } from "../../utils/api/ApiRequests"
import { GetAllEmployees } from "../../utils/api/DropdownFiltersApi"
import { usePostRequest } from "../../hooks/usePostRequest"
import { getIsAdminLogin, capitalizeEachWord } from "../../utils/Common"
import toast from "react-hot-toast"

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false)
  const values = Form.useWatch([], form)
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true)
        },
        () => {
          setSubmittable(false)
        }
      )
  }, [values])

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  )
}

export default function Overtime() {
  const [form] = Form.useForm()
  const isAdminLogin = getIsAdminLogin()
  // const employeeId = getEmployeeId();
  // const navigate = useNavigate();
  // const [content, setContent] = useState("");
  const [tableData, setTableData] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [clickedRowData, setClickedRowData] = useState({})
  const [projectOptions, setProjectOptions] = useState([])
  const [projectDetails, setProjectDetails] = useState({})
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const employeeOptions = GetAllEmployees()
  const dateFormat = "YYYY-MM-DD"
  const [searchTerm, setSearchTerm] = useState("")
  const [reason, setReason] = useState("")

  const { callPost, isLoading } = usePostRequest(() => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowViewModal(false)
    setShowConfirmDelete(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  })

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    status: "pending",
  })

  const tableColumns = [
    {
      title: "Employee",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date, record) => (
        <div>{moment(date).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Overtime",
      dataIndex: "overtime",
      key: "overtime",
      render: (overtime, record) => (
        <div>{moment(overtime, "hh:mm A").format("hh:mm A")}</div>
      ),
    },
    {
      title: "No. of Overtime Hours",
      dataIndex: "ot_hours",
      key: "ot_hours",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (data) => <div>{capitalizeEachWord(data)}</div>,
    },
  ]

  const onFinish = async (formInputs) => {
    // Format the date and time inputs
    formInputs.date = formInputs.date.format(dateFormat)
    formInputs.overtime = formInputs.overtime.format("HH:mm:ss")
    formInputs.status = "pending"
    formInputs.is_timein = formInputs.is_timein ? "1" : "0"

    // Convert the selected date to a day of the week (0 = Sunday, 6 = Saturday)
    const selectedDate = moment(formInputs.date, dateFormat)
    const dayOfWeek = selectedDate.day() // .day() returns 0 for Sunday

    if (dayOfWeek === 0) {
      // If the selected date is Sunday, skip the time validation and proceed
      callPost("overtimes/add", formInputs, false)
    } else {
      // Validate if overtime is beyond the expected timeout
      const dateToday = moment().format("YYYY-MM-DD")
      const overtime = new Date(`${dateToday} ${formInputs.overtime}`)
      const timeOut = projectDetails.second_timeout
        ? new Date(`${dateToday} ${projectDetails.second_timeout}`)
        : new Date(`${dateToday} ${projectDetails.first_timeout}`)
      const timeIn = new Date(`${dateToday} ${projectDetails.first_timein}`)

      if (overtime >= timeIn && overtime <= timeOut) {
        toast.error("Invalid overtime request")
      } else {
        callPost("overtimes/add", formInputs, false)
      }
    }
  }

  const onEdit = async (formInputs) => {
    formInputs.date = formInputs.date.format(dateFormat)
    formInputs.overtime = formInputs.overtime.format("HH:mm:ss")
    formInputs.is_timein = formInputs.is_timein ? "1" : "0"

    callPost(`overtimes/update/` + clickedRowData.id, formInputs, false)
  }

  const confirmApprove = (overtime, status) => {
    modal.confirm({
      title: `Are you sure you want to ${status} ${overtime.full_name}'s overtime?`,
      icon: <CheckCircleOutlined />,
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => handleApprove(overtime.id, status),
    })
  }

  function handleApprove(passedId, status) {
    const newStatus = `${status}d`
    callPost(
      "overtimes/approve",
      { overtime_id: passedId, status: newStatus },
      false
    )
    setShowViewModal(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  }

  // const confirmDelete = (overtime) => {
  //   modal.confirm({
  //     title: `Are you sure you want to delete this overtime"?`,
  //     icon: <ExclamationCircleOutlined />,
  //     content: "You cannot undo this action.",
  //     okText: "Delete",
  //     cancelText: "Cancel",
  //     onOk: () => handleDelete(overtime.id),
  //     okType: "danger",
  //   });
  // };

  function handleDelete(passedId) {
    callPost(
      "overtimes/delete",
      { overtime_id: passedId, remarks: reason },
      false
    )
    resetValues()
  }

  function resetValues() {
    form.resetFields()
    setReason("")
    setProjectOptions([])
    setProjectDetails({})
  }

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }))
    }
  }

  const handleRowClick = (rowData) => {
    setClickedRowData(rowData)
    fetchProjectOptions(rowData.employee_id)
    fetchProjectDetails(rowData.project_id)
    const formattedValues = { ...rowData }
    formattedValues.date = formattedValues.date
      ? dayjs(formattedValues.date, dateFormat)
      : ""
    formattedValues.overtime = moment(rowData.overtime, "hh:mm A")
    formattedValues.is_timein = rowData.is_timein === "1" ? true : false
    form.setFieldsValue(formattedValues)
    setShowViewModal(true)
  }

  const rowProps = (record) => ({
    onClick: () => handleRowClick(record),
  })

  async function fetchProjectOptions(passedId) {
    setProjectOptions([])
    setProjectDetails({})
    form.setFieldValue("project_id", null)
    const response = await UseGetRequest("employees/get_projects", {
      employee_id: passedId,
    })
    if (response.data) {
      const res = (response.data.employee_projects ?? []).map((row) => {
        return {
          ...row,
          value: row.project_id,
          label: row.project,
        }
      })
      setProjectOptions(res)
    }
  }

  async function fetchProjectDetails(id) {
    const response = await UseGetRequest("projects/get", { project_id: id })
    if (response.data) {
      setProjectDetails(response.data.data[0])
    }
  }

  const handleDateFilter = (e) => {
    if (e) {
      setFilterConfig((prev) => ({
        ...prev,
        date_from: e[0].format(dateFormat),
        date_to: e[1].format(dateFormat),
      }))
    }
  }

  React.useEffect(() => {
    async function fetchTableData() {
      setIsFetching(true)
      setTableData([])
      const response = await UseGetRequest("overtimes/get", filterConfig)
      if (response.data) {
        setTableData(response.data.overtimes)
      }
      setIsFetching(false)
    }

    fetchTableData()
  }, [filterConfig])

  return (
    <Navbar
      activeNav="overtime"
      pageTitle="Overtimes"
      pageButtonTitle="Add Overtime"
      onClickButton={() => {
        setShowAddModal(true)
        setShowViewModal(false)
      }}
      searchPlaceholder="employee"
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      isManager
      searchable={isAdminLogin}
      withDateRangePicker
      onDateChange={handleDateFilter}
    >
      <Tabs
        defaultActiveKey="pending"
        className="mt-4"
        items={[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "disapproved", label: "Disapproved" },
          { key: "", label: "All" },
        ]}
        type="card"
        onChange={(e) => setFilterConfig((prev) => ({ ...prev, status: e }))}
      />
      <Table
        className=""
        rowKey="id"
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching}
        onRow={(record) => ({
          ...rowProps(record),
          style: { cursor: "pointer" },
        })}
      />
      <Modal
        maskClosable={false}
        title={`${showAddModal ? "Add" : "Edit"} Overtime`}
        centered
        open={showAddModal || showEditModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false)
          setShowEditModal(false)
          resetValues()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={showAddModal ? onFinish : onEdit}
          initialValues={{ is_timein: false }}
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
                placeholder="Select Employee"
              >
                <Select
                  options={employeeOptions.slice(1)}
                  filterOption={(input, option) =>
                    option.label?.toLowerCase().includes(input?.toLowerCase())
                  }
                  onChange={(e) => fetchProjectOptions(e)}
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Row className="pb-0-force">
                <Form.Item
                  name="project_id"
                  label="Project"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                    },
                  ]}
                  className="w-100"
                  placeholder="Select Project"
                >
                  <Select
                    options={projectOptions}
                    onChange={(e, obj) => {
                      setProjectDetails({ ...obj, id: e })
                    }}
                  />
                </Form.Item>
              </Row>
            </Col>

            <Col xs={24} lg={10}>
              <Form.Item
                name="date"
                label="Date"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={10}>
              <Form.Item
                name="overtime"
                label="Overtime"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <TimePicker format={"hh:mm A"} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={4}>
              <Form.Item name="is_timein" label="" valuePropName="checked">
                <Checkbox className="ms-2 mt-4">Is Time-In</Checkbox>
              </Form.Item>
            </Col>

            <Col xs={24} lg={12} className="default-bg p-2">
              <Row className="bold primary-color">
                {projectDetails.label} Schedule
              </Row>
              {projectDetails && projectDetails.id && (
                <div className="">
                  <Row className="d-flex justify-content-between ms-3">
                    <Col className="smaller-text">
                      Time In 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timein,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                    <Col className="smaller-text">
                      Time Out 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timeout,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-between ms-3">
                    {projectDetails.second_timein && (
                      <Col className="smaller-text">
                        Time In 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timein,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                    {projectDetails.second_timeout && (
                      <Col className="smaller-text">
                        Time Out 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timeout,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowAddModal(false)
                    setShowEditModal(false)
                    resetValues()
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Review Overtime"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false)
          resetValues()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="editForm"
          layout="vertical"
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select
                  options={employeeOptions.slice(1)}
                  searchable
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="project_id"
                label="Project"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={projectOptions} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={10}>
              <Form.Item name="date" label="Date">
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={10}>
              <Form.Item name="overtime" label="Overtime">
                <TimePicker format={"hh:mm A"} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={4}>
              <Form.Item name="is_timein" label="" valuePropName="checked">
                <Checkbox className="ms-2 mt-4" disabled>
                  Is Time-In
                </Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12} className="default-bg p-2">
              <Row className="bold primary-color">
                {projectDetails.project} Schedule
              </Row>
              {projectDetails && projectDetails.id && (
                <div className="">
                  <Row className="d-flex justify-content-between ms-3">
                    <Col className="smaller-text">
                      Time In 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timein,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                    <Col className="smaller-text">
                      Time Out 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timeout,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-between ms-3">
                    {projectDetails.second_timein && (
                      <Col className="smaller-text">
                        Time In 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timein,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                    {projectDetails.second_timeout && (
                      <Col className="smaller-text">
                        Time Out 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timeout,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end mt-4">
              <Button
                danger
                onClick={() => {
                  setShowViewModal(false)
                  resetValues()
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="text"
                onClick={() => {
                  setShowViewModal(false)
                  setShowEditModal(true)
                }}
                icon={<EditOutlined />}
                disabled={clickedRowData.status === "approved"}
              >
                Edit
              </Button>
              {isAdminLogin && (
                <Button
                  type="text"
                  onClick={() => {
                    confirmApprove(clickedRowData, "approve")
                  }}
                  icon={<CheckCircleOutlined />}
                  disabled={clickedRowData.status === "approved"}
                >
                  Approve
                </Button>
              )}
              <Button
                type="text"
                onClick={() => {
                  confirmApprove(clickedRowData, "disapprove")
                }}
                icon={<CloseCircleOutlined />}
                disabled={clickedRowData.status === "disapproved"}
                style={{ color: "red" }}
              >
                Disapprove
              </Button>
              <Button
                type="text"
                danger
                onClick={() => {
                  setShowConfirmDelete(true)
                  // confirmDelete(clickedRowData);
                }}
                icon={<DeleteOutlined />}
                disabled={clickedRowData.status === "approved"}
              >
                Delete
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={fileSizeExceeded}
        title={""}
        footer={null}
        onCancel={() => setFileSizeExceeded(false)}
      >
        File size must not exceed 5MB.
      </Modal>

      <Modal
        footer={null}
        open={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
      >
        <div className="p-2">
          <Row>
            <Title level={5}>
              <ExclamationCircleOutlined /> Are you sure you want to delete this
              overtime?
            </Title>
          </Row>
          <Row className="ms-3 mt-1">You cannot undo this action.</Row>
          <Row className="ms-3">
            <Input
              placeholder="Enter reason for deletion"
              onChange={(e) => setReason(e.target.value)}
              value={reason}
            />
          </Row>
          <Row className="mt-4 d-flex justify-content-end">
            <Space>
              <Button onClick={() => setShowConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                danger
                onClick={() => handleDelete(clickedRowData.id)}
                disabled={reason.trim() === ""}
              >
                Confirm
              </Button>
            </Space>
          </Row>
        </div>
      </Modal>
      {contextHolder}
    </Navbar>
  )
}
